import {
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';

import { ToastVariant } from '@/shared/ui/toast/toast';
import { ToastContainer } from './toast-container/toast-container';
import { ToastItem, ToastPosition } from './toast.types';

export type { ToastItem };

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly appRef = inject(ApplicationRef);
  private nextId = 0;
  private containerMounted = false;

  private readonly toastsSignal = signal<ToastItem[]>([]);
  private readonly positionSignal = signal<ToastPosition>('top-right');

  readonly toasts = this.toastsSignal.asReadonly();
  readonly position = this.positionSignal.asReadonly();

  show(
    variant: ToastVariant,
    message: string,
    options?: { duration?: number; showCloseButton?: boolean; position?: ToastPosition },
  ): void {
    this.mountContainer();
    if (options?.position) this.positionSignal.set(options.position);
    const id = ++this.nextId;
    this.toastsSignal.update((ts) => [
      ...ts,
      {
        id,
        variant,
        message,
        duration: options?.duration ?? 5000,
        showCloseButton: options?.showCloseButton ?? true,
      },
    ]);
  }

  dismiss(id: number): void {
    this.toastsSignal.update((ts) => ts.filter((t) => t.id !== id));
  }

  private mountContainer(): void {
    if (this.containerMounted) return;
    this.containerMounted = true;

    const ref = createComponent(ToastContainer, { environmentInjector: this.appRef.injector });
    ref.setInput('toasts', this.toastsSignal.asReadonly());
    ref.setInput('onDismiss', (id: number) => this.dismiss(id));
    ref.setInput('position', this.positionSignal.asReadonly());
    this.appRef.attachView(ref.hostView);
    const el = (ref.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    document.body.appendChild(el);
  }
}
